import { useState, useEffect, useCallback, useRef, useMemo } from "react";

const events = [
  "mousemove",
  "mousedown",
  "keypress",
  "DOMMouseScroll",
  "mousewheel",
  "touchmove",
  "keydown",
  "scroll",
  "MSPointerMove",
  "blur",
  "focus",
] as const;

/**
 * Custom React hook to detect user inactivity (idle state) and optionally show a warning before becoming idle.
 * @author @CorradoSurmanowicz
 * @param idleTime - The idle time in minutes before the user is considered idle. If `null` or `undefined`, the hook is disabled.
 * @param warn - Whether to show a warning before the user becomes idle.
 * @param onActivityCallback - Optional callback function invoked on user activity.
 * @returns An object containing:
 *   - `isIdle`: `boolean` indicating if the user is currently idle.
 *   - `showWarning`: `boolean` indicating if the warning should be shown.
 *   - `deltaT`: The time in milliseconds between the warning and the idle state, or `undefined` if not applicable.
 *
 * @remarks
 * - The hook listens for user activity events (e.g., mouse, keyboard) and resets timers accordingly.
 * - The warning is shown at 60% of the `idleTime` duration if `warn` is `true`.
 * - Timers are cleared on unmount or when dependencies change.
 */
export const useIdle = (idleTime: number | null | undefined, warn: boolean, onActivityCallback?: () => void) => {
  const [isIdle, setIsIdle] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const warningTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Calculate warning time as 60% of idleTime, if idleTime is defined
  const idleTimeInMilliseconds = useMemo(() => {
    if (idleTime) {
      return idleTime * 60000; // Convert minutes to milliseconds
    }
  }, [idleTime]);

  const warningTimeInMilliseconds = useMemo(() => {
    if (idleTimeInMilliseconds) {
      return idleTimeInMilliseconds * (2 / 3); // 60% of idleTime
    }
  }, [idleTimeInMilliseconds]);

  const deltaT = useMemo(
    () =>
      idleTimeInMilliseconds && warningTimeInMilliseconds
        ? idleTimeInMilliseconds - warningTimeInMilliseconds
        : undefined,
    [idleTimeInMilliseconds, warningTimeInMilliseconds]
  );

  const handleActivity = useCallback(() => {
    if (idleTimeInMilliseconds) {
      setIsIdle(false);
      setShowWarning(false);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      onActivityCallback?.();
      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true);
      }, idleTimeInMilliseconds);

      if (warn && warningTimeInMilliseconds)
        warningTimerRef.current = setTimeout(() => {
          setShowWarning(true);
        }, warningTimeInMilliseconds);
    }
  }, [idleTimeInMilliseconds, onActivityCallback, warn, warningTimeInMilliseconds]);

  useEffect(() => {
    if (idleTimeInMilliseconds) events.forEach((event) => window.addEventListener(event, handleActivity));
    return () => {
      if (idleTimeInMilliseconds) {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        events.forEach((event) => window.removeEventListener(event, handleActivity));
      }
    };
  }, [handleActivity, idleTimeInMilliseconds]);

  return { isIdle, showWarning, deltaT };
};
